import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, initMultiMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = event => ensureInteger(event.id);

export const eventClientSlice = createSlice({
    name: 'clientEvents',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            ensureArray(action.payload.data).forEach(event => state.data[key(event)] = event);
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(e => state.data[key(e)] = e);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(event => delete state.data[key(event)])
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => eventClientSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'event_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'event_one');

const fetchClientEvent = aggregatingFetch(
    id => network => {
        return network.post('/api/v2/clientEvents', [id], data => eventClientSlice.actions.set({ data, ids: [id] }), visualizeFailure('get'))
    },
    ids => network => {
        return network.post('/api/v2/clientEvents', Array.from(ids), data => eventClientSlice.actions.set( {data, ids }), visualizeFailure('get'))
    },
    { primitiveKey: true }
);


// #region selectors
const selectEventData = state => state.clientEvents.data;
const selectEvent = id => createSelector([selectEventData], events => events[id]);
const selectEvents = ids => createSelector([selectEventData], events => ids.reduce((res, id) => {
    if (events[id]) {
        res.push(events[id]);
    }
    return res;
}, []));

const listen = {
    eventClientCreated: (event, { dispatch, websocket }) => {
        markFetched(websocket, 'event', key(event));
        dispatch(eventClientSlice.actions.addOrUpdate(event))
    },
    eventClientUpdated: (event, { dispatch }) => dispatch(eventClientSlice.actions.addOrUpdate(event)),
    eventClientRemoved: (event, { dispatch }) => dispatch(eventClientSlice.actions.remove(event))
}

// have to use the same factory for the single and multiple gets so that single and multiple deps are unified
const eventInitByID = initMapFactory(fetchClientEvent, listen);
registerEntityBasedFetch('event', eventInitByID);

const options =  { 
    cleanup: { root: eventClientSlice.name, all: state => Object.values(selectEventData(state)), removeAll: eventClientSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('client roster viewer')
}

export const [useClientEvent, realizeClientEvent] = dataHookWithCredentialsFactory(selectEvent, eventInitByID, { ...options, defaultValue: null });
export const [useClientEvents] = dataHookWithCredentialsFactory(selectEvents, initMultiMapFactory(eventInitByID, listen), options);

const reducer = { name: eventClientSlice.name, reducer: eventClientSlice.reducer }
export default reducer;